import { toast } from '@quinyx/ui';
import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';

import {
  createCustomerApi,
  createLicenseApi,
  createScriveLicensePeriodApi,
  deleteScriveLicensePeriodApi,
  updateScriveLicensePeriodApi,
  createZoinedApi,
  deleteCustomer as deleteCustomerApi,
  updateCustomerDeleteBy as updateCustomerDeleteByApi,
  getCustomerApi,
  getCustomerCreationProgressApi,
  getCustomerCreationsInProgress,
  getDeletedCustomersQueue,
  getLicenseApi,
  getPaginatedCustomersApi,
  getRemovableCustomers,
  getZoinedApi,
  reprovisionCustomerApi,
  updateCustomerApi,
  updateLicenseApi,
  updateZoinedApi,
  undoDeleteCustomer as undoDeleteCustomerApi,
  getScriveLicensesApi,
} from '../../api/endpoints/customer';
import { IErrorResponse, IPaginationParams } from '@/api/interfaces';
import {
  ICreateCustomerProgress,
  ICustomer,
  ICustomerCreatePayload,
  ICustomerPagination,
  IDeletedCustomer,
  IRemovableCustomer,
  ICustomerError,
  IScriveLicensePeriod,
} from '@/api/interfaces/customer';
import {
  createPaginationQueryString,
  extractPaginationSortFromQuery,
} from '@/utils';
import i18n from '@/i18n';
import { DeletedCustomerQueueStatus } from '@/constants/customer-deletion';

const getCustomerErrorMessage = (error: ICustomerError, fallback: string) =>
  error?.response?.data?.additionalErrorParameters?.exceptionMessage ||
  error?.response?.data?.message ||
  fallback;

const notifyError = (error: ICustomerError) =>
  toast({
    message: i18n.t(getCustomerErrorMessage(error, 'toast.somethingWentWrong')),
    type: 'negative',
  });

const notifySuccess = (message: string) =>
  toast({
    message: i18n.t(message),
    type: 'positive',
  });

export const useDeletedCustomersQueue = (status?: DeletedCustomerQueueStatus) =>
  useQuery<IDeletedCustomer[], IErrorResponse>({
    queryKey: ['deletedCustomersQueue', status],
    queryFn: () => getDeletedCustomersQueue(status),
    useErrorBoundary: false,
  });

export const useRemovableCustomers = () =>
  useQuery<IRemovableCustomer[], IErrorResponse>({
    queryKey: ['removableCustomers'],
    queryFn: getRemovableCustomers,
    useErrorBoundary: false,
  });

export const useCreateCustomer = () => {
  const {
    mutateAsync: createCustomer,
    isPending: isLoading,
    error,
  } = useMutation<
    ICreateCustomerProgress,
    ICustomerError,
    ICustomerCreatePayload
  >({
    mutationFn: (customer) => createCustomerApi(customer),
  });

  return {
    createCustomer,
    isLoading,
    error,
  };
};

export const useCustomerCreationsInProgress = () =>
  useQuery<ICreateCustomerProgress[], IErrorResponse>({
    queryKey: ['createCustomerProgressList'],
    queryFn: getCustomerCreationsInProgress,
    useErrorBoundary: false,
  });

export const useCustomerCreationProgress = () => {
  const { mutateAsync: getCustomerCreationProgress, isPending: isLoading } =
    useMutation<ICreateCustomerProgress, IErrorResponse, string>({
      mutationFn: (id) => getCustomerCreationProgressApi(id),
    });

  return {
    getCustomerCreationProgress,
    isLoading,
  };
};

export const useCreateLicense = () => {
  const queryClient = useQueryClient();

  const { mutate: createLicense, ...rest } = useMutation({
    mutationFn: ([domainId, payload]: Parameters<typeof createLicenseApi>) =>
      createLicenseApi(domainId, payload),
    onSuccess: (_data, [domainId]) => {
      notifySuccess('toast.licenseCreated');
      queryClient.invalidateQueries({ queryKey: [`license_${domainId}`] });
    },
    onError: notifyError,
  });

  return { createLicense, ...rest };
};

export const useGetLicense = (domainId: string) =>
  useQuery({
    queryKey: [`license_${domainId}`],
    queryFn: () => getLicenseApi(domainId),
    retry: false,
  });

export const useUpdateLicense = () => {
  const queryClient = useQueryClient();

  const { mutate: updateLicense, ...rest } = useMutation({
    mutationFn: ([domainId, update]: Parameters<typeof updateLicenseApi>) =>
      updateLicenseApi(domainId, update),
    onSuccess: (_data, [domainId]) => {
      notifySuccess('toast.licenseUpdated');
      queryClient.invalidateQueries({ queryKey: [`license_${domainId}`] });
    },
    onError: notifyError,
  });

  return { updateLicense, ...rest };
};

export const useGetScriveLicenses = <TData = IScriveLicensePeriod[]>(
  domainId: string,
  options?: Omit<
    UseQueryOptions<IScriveLicensePeriod[], IErrorResponse, TData>,
    'queryKey' | 'queryFn'
  > & { showDeleted?: boolean },
) => {
  const { showDeleted = false, ...queryOptions } = options ?? {};

  return useQuery<IScriveLicensePeriod[], IErrorResponse, TData>({
    queryKey: ['scrive_licenses', domainId, showDeleted],
    queryFn: () => getScriveLicensesApi(domainId, showDeleted),
    retry: false,
    ...queryOptions,
  });
};

export const useCreateScriveLicensePeriod = () => {
  const queryClient = useQueryClient();

  const { mutate: createScriveLicensePeriod, ...rest } = useMutation({
    mutationFn: ([domainId, payload]: Parameters<
      typeof createScriveLicensePeriodApi
    >) => createScriveLicensePeriodApi(domainId, payload),
    onSuccess: (_data, [domainId]) => {
      notifySuccess('toast.scriveLicensePeriodCreated');
      queryClient.invalidateQueries({
        queryKey: ['scrive_licenses', domainId],
      });
    },
    onError: notifyError,
  });

  return { createScriveLicensePeriod, ...rest };
};

export const useUpdateScriveLicensePeriod = () => {
  const queryClient = useQueryClient();

  const { mutate: updateScriveLicensePeriod, ...rest } = useMutation({
    mutationFn: ([domainId, id, payload]: Parameters<
      typeof updateScriveLicensePeriodApi
    >) => updateScriveLicensePeriodApi(domainId, id, payload),
    onSuccess: (_data, [domainId]) => {
      notifySuccess('toast.scriveLicensePeriodUpdated');
      queryClient.invalidateQueries({
        queryKey: ['scrive_licenses', domainId],
      });
    },
    onError: notifyError,
  });

  return { updateScriveLicensePeriod, ...rest };
};

export const useDeleteScriveLicensePeriod = () => {
  const queryClient = useQueryClient();

  const { mutate: deleteScriveLicensePeriod, ...rest } = useMutation({
    mutationFn: ([domainId, id]: Parameters<
      typeof deleteScriveLicensePeriodApi
    >) => deleteScriveLicensePeriodApi(domainId, id),
    onSuccess: (_data, [domainId]) => {
      notifySuccess('toast.scriveLicensePeriodDeleted');
      queryClient.invalidateQueries({
        queryKey: ['scrive_licenses', domainId],
      });
    },
    onError: notifyError,
  });

  return { deleteScriveLicensePeriod, ...rest };
};

export const useGetZoined = (domainId: string) =>
  useQuery({
    queryKey: [`zoined_${domainId}`],
    queryFn: () => getZoinedApi(domainId),
    retry: false,
  });

export const useCreateZoined = () => {
  const queryClient = useQueryClient();

  const { mutate: createZoined, ...rest } = useMutation({
    mutationFn: ([domainId, payload]: Parameters<typeof createZoinedApi>) =>
      createZoinedApi(domainId, payload),
    onSuccess: (_data, [domainId]) => {
      notifySuccess('toast.zoinedConfigCreated');
      queryClient.invalidateQueries({ queryKey: [`zoined_${domainId}`] });
    },
    onError: notifyError,
  });

  return { createZoined, ...rest };
};

export const useUpdateZoined = () => {
  const queryClient = useQueryClient();

  const { mutate: updateZoined, ...rest } = useMutation({
    mutationFn: ([domainId, update]: Parameters<typeof updateZoinedApi>) =>
      updateZoinedApi(domainId, update),
    onSuccess: (_data, [domainId]) => {
      notifySuccess('toast.zoinedConfigUpdated');
      queryClient.invalidateQueries({ queryKey: [`zoined_${domainId}`] });
    },
    onError: notifyError,
  });

  return { updateZoined, ...rest };
};

const getCorrectPage = (page?: number) => page && page - 1;

export interface UsePaginatedCustomersOptions {
  hideReadyForDeletion?: boolean;
}

export const usePaginatedCustomers = ({
  hideReadyForDeletion,
}: UsePaginatedCustomersOptions = {}) => {
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const page = searchParams.get('page')
    ? Number(searchParams.get('page'))
    : undefined;
  const size = searchParams.get('size')
    ? Number(searchParams.get('size'))
    : undefined;
  const name = searchParams.get('name') || undefined;
  const sort = searchParams.get('sort') || undefined;

  const [paginatedCustomers, setPaginatedCustomers] =
    useState<ICustomerPagination>();

  const { isLoading: initialLoading, data: initialData } = useQuery({
    queryKey: ['paginatedCustomers', hideReadyForDeletion],
    queryFn: () => {
      const initialPaginationQueryString = createPaginationQueryString({
        page: getCorrectPage(page),
        size,
        name,
        sort: extractPaginationSortFromQuery(String(sort)),
        hideReadyForDeletion,
      });
      return getPaginatedCustomersApi(`?${initialPaginationQueryString}`);
    },
    useErrorBoundary: false,
  });

  useEffect(() => {
    if (initialData) {
      setPaginatedCustomers(initialData);
    }
  }, [initialData]);

  const {
    mutate: getPaginatedCustomers,
    isPending,
    error,
    reset,
    status,
  } = useMutation({
    mutationFn: (queryParams: IPaginationParams) => {
      const newQueryString = createPaginationQueryString({
        ...queryParams,
        page: getCorrectPage(queryParams.page),
        hideReadyForDeletion,
      });
      return getPaginatedCustomersApi(`?${newQueryString}`);
    },
    onSuccess: (data) => setPaginatedCustomers(data),
    onError: notifyError,
    useErrorBoundary: false,
  });

  return {
    getPaginatedCustomers,
    paginatedCustomers: paginatedCustomers ?? initialData,
    isLoading: initialLoading || isPending,
    error,
    reset,
    status,
  };
};

export const useGetCustomer = (domainId: string, enabled = true) => {
  const query = useQuery({
    queryKey: [`customer_${domainId}`],
    queryFn: () => getCustomerApi(domainId),
    enabled,
  });

  useEffect(() => {
    if (query.error) {
      notifyError(query.error as ICustomerError);
    }
  }, [query.error]);

  return query;
};

export const useUpdateCustomer = () => {
  const queryClient = useQueryClient();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  // Create pagination query without customerId
  const paginationQuery = new URLSearchParams(searchParams);
  paginationQuery.delete('customerId');

  const {
    mutateAsync: updateCustomer,
    error,
    ...rest
  } = useMutation({
    mutationFn: ([domainId, payload]: Parameters<typeof updateCustomerApi>) =>
      updateCustomerApi(domainId, payload),
    onSuccess: (_data, [domainId]) => {
      notifySuccess('toast.customerUpdated');
      queryClient.invalidateQueries({ queryKey: [`customer_${domainId}`] });
    },
    onError: notifyError,
  });

  return { updateCustomer, error, ...rest };
};

export const useReprovisionCustomer = () => {
  const { mutateAsync: reprovisionCustomer, isPending } = useMutation<
    void,
    ICustomerError,
    string
  >({
    mutationFn: (domainId) => reprovisionCustomerApi(domainId),
    onSuccess: () => notifySuccess('toast.customerReprovisioned'),
    onError: notifyError,
  });

  return { reprovisionCustomer, isPending };
};

export { useRecentlyVisitedCustomers } from './useRecentlyVisitedCustomers';

export const useDeleteCustomer = () => {
  const queryClient = useQueryClient();

  const { mutateAsync: deleteCustomer, isPending: isLoading } = useMutation<
    void,
    ICustomerError,
    [ICustomer, string, string?]
  >({
    mutationFn: async ([customer, deleteBy]) => {
      if (!customer || !deleteBy) {
        throw new Error('Customer and delete by are required');
      }

      return deleteCustomerApi([{ id: customer.customerId, deleteBy }]);
    },
    onSuccess: (_data, [_, __, domainId]) => {
      queryClient.invalidateQueries({ queryKey: ['removableCustomers'] });
      queryClient.invalidateQueries({ queryKey: ['deletedCustomersQueue'] });
      if (domainId) {
        queryClient.invalidateQueries({ queryKey: [`customer_${domainId}`] });
      }
    },
    onError: notifyError,
  });

  return {
    deleteCustomer,
    isLoading,
  };
};

export const useUpdateCustomerDeleteBy = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: updateCustomerDeleteBy, isPending: isLoading } =
    useMutation<void, ICustomerError, [number, string]>({
      mutationFn: async ([customerId, deleteBy]) => {
        return updateCustomerDeleteByApi(customerId, deleteBy);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['removableCustomers'] });
        queryClient.invalidateQueries({ queryKey: ['deletedCustomersQueue'] });
      },
      onError: notifyError,
    });

  return { updateCustomerDeleteBy, isLoading };
};

export const useUndoDeleteCustomer = () => {
  const queryClient = useQueryClient();

  const { mutateAsync: undoDeleteCustomer, isPending: isLoading } = useMutation<
    void,
    ICustomerError,
    [number, string?]
  >({
    mutationFn: async ([customerId]) => {
      if (!customerId) {
        throw new Error('Customer ID is required');
      }

      return undoDeleteCustomerApi(customerId);
    },
    onSuccess: (_data, [_, domainId]) => {
      queryClient.invalidateQueries({ queryKey: ['removableCustomers'] });
      queryClient.invalidateQueries({ queryKey: ['deletedCustomersQueue'] });
      if (domainId) {
        queryClient.invalidateQueries({ queryKey: [`customer_${domainId}`] });
      }
    },
    onError: notifyError,
  });

  return {
    undoDeleteCustomer,
    isLoading,
  };
};
